import { ArrowRight, Check, HeartHandshake, Lock } from 'lucide-react'

import { cn } from '@/lib/utils'

const amounts: { value: string; selected?: boolean }[] = [
  { value: '$25' },
  { value: '$50' },
  { value: '$100', selected: true },
  { value: '$250' },
  { value: '$500' },
  { value: 'Other' },
]

const paymentMethods = ['Cards', 'Eligible wallets'] as const

export function HeroMockup() {
  return (
    <figure aria-label="Example donation form" className="relative mx-auto w-full max-w-[460px]">
      {/* Ambient glows */}
      <div className="absolute -bottom-12 -left-10 h-48 w-48 rounded-full bg-teal/25 blur-3xl" />
      <div className="absolute -right-10 -top-6 h-48 w-48 rounded-full bg-sky/25 blur-3xl" />

      {/* Donation form card */}
      <div className="relative overflow-hidden rounded-[28px] border border-white/70 bg-white shadow-[0_40px_100px_rgba(15,35,78,0.22)]">
        {/* Branded header */}
        <div className="bg-[linear-gradient(135deg,#2563EB,#0EA5E9)] px-6 pb-7 pt-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
                <HeartHandshake className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold leading-tight">Hope Foundation</p>
                <p className="text-[11px] text-white/70">Clean water for every family</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold text-white/90">
              <Lock className="h-3 w-3" />
              Secure
            </div>
          </div>
          <p className="mt-5 text-xl font-semibold leading-tight">Make a donation</p>
        </div>

        {/* Form body */}
        <div className="space-y-5 px-6 py-6">
          {/* Frequency toggle */}
          <div className="grid grid-cols-2 gap-1 rounded-2xl border border-line bg-surface-strong p-1">
            <div className="rounded-xl py-2 text-center text-sm font-semibold text-ink-muted">
              One-time
            </div>
            <div className="rounded-xl bg-white py-2 text-center text-sm font-semibold text-ink shadow-[0_8px_20px_rgba(15,35,78,0.08)]">
              Monthly
            </div>
          </div>

          {/* Amounts */}
          <div className="grid grid-cols-3 gap-2.5">
            {amounts.map((amount) => (
              <div
                key={amount.value}
                className={cn(
                  'rounded-2xl py-3.5 text-center text-base font-semibold transition',
                  amount.selected
                    ? 'bg-[linear-gradient(135deg,#2563EB,#0EA5E9)] text-white shadow-[0_14px_28px_rgba(37,99,235,0.28)]'
                    : 'border border-line bg-white text-ink',
                )}
              >
                {amount.value}
              </div>
            ))}
          </div>

          {/* Custom amount input */}
          <div className="flex items-center gap-2 rounded-2xl border border-line bg-surface-strong px-4 py-3.5">
            <span className="text-base font-semibold text-ink-muted">$</span>
            <span className="text-base font-semibold text-ink">100</span>
            <span className="ml-auto text-xs font-medium text-ink-muted">/ month</span>
          </div>

          {/* Donate button */}
          <div
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-night py-4 text-sm font-semibold text-white"
          >
            Donate $100 monthly
            <ArrowRight className="h-4 w-4" />
          </div>

          {/* Trust footer */}
          <div className="flex items-center justify-between gap-3 pt-1">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-ink-muted">
              <Lock className="h-3.5 w-3.5 text-teal" />
              Powered by Stripe
            </div>
            <div className="flex items-center gap-1.5">
              {paymentMethods.map((method) => (
                <span
                  key={method}
                  className="rounded-md border border-line bg-white px-2 py-1 text-[10px] font-semibold text-ink-soft"
                >
                  {method}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating: new donor toast. -top-10 keeps its bottom edge clear of the
          "Secure" pill in the card header (it sat over the pill at -top-6). */}
      <div className="absolute -top-10 -right-4 hidden animate-float rounded-2xl border border-line bg-white px-4 py-3 shadow-soft sm:block">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-success/10 text-success">
            <Check className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-semibold text-ink">New monthly donor</p>
            <p className="text-xs text-ink-muted">$100 • recurring</p>
          </div>
        </div>
      </div>
    </figure>
  )
}
